/** Flag value that asks edge-lambda-service to mint a presigned GET URL. */
export const PRESIGNED_INTEGRATION_PREFIX = '/presigned/';

const MAX_PRESIGNED_PATH_LENGTH = 2048;

function decodeSegment(segment: string): string {
  if (!/%[0-9A-Fa-f]{2}/.test(segment)) return segment;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * True when `value` is a path under `/presigned/` that can be requested from
 * edge-lambda-service. Rejects traversal, queries, and anything that would
 * change the request host.
 */
export function isPresignedIntegrationPath(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed.startsWith(PRESIGNED_INTEGRATION_PREFIX)) return false;
  if (trimmed.length > MAX_PRESIGNED_PATH_LENGTH) return false;
  if (trimmed.includes('\\') || trimmed.includes('?') || trimmed.includes('#') || trimmed.includes('://')) {
    return false;
  }
  if (/[\0\r\n]/.test(trimmed)) return false;

  const segments = trimmed.split('/').map(decodeSegment);
  if (segments[0] !== '' || segments[1] !== 'presigned') return false;
  const rest = segments.slice(2);
  if (rest.length === 0) return false;
  return rest.every(
    (segment) =>
      segment !== ''
      && segment !== '.'
      && segment !== '..'
      && !segment.includes('/')
      && !segment.includes('\\'),
  );
}

/**
 * Object key to send as `sourceFilePath` on `POST /edge-lambda-service/download`.
 * `/presigned/` is only the flag marker; edge-lambda has no such route and
 * would look up that prefix as a processed-bucket key.
 */
export function presignedIntegrationObjectKey(value: string): string | null {
  if (!isPresignedIntegrationPath(value)) return null;
  const segments = value.trim().split('/').map(decodeSegment);
  return segments.slice(2).join('/');
}

/**
 * Edge-lambda routes a flag may name as `/presigned/<route>`. These are not
 * object keys: `/presigned/sockpuppet-upload-url` is
 * `POST /edge-lambda-service/sockpuppet-upload-url`.
 */
const PRESIGNED_EDGE_ROUTES = new Set(['sockpuppet-upload-url']);

export function presignedIntegrationEdgeRoute(value: string): string | null {
  const key = presignedIntegrationObjectKey(value);
  if (!key || key.includes('/')) return null;
  return PRESIGNED_EDGE_ROUTES.has(key) ? key : null;
}

/** Percent-encode each segment of a validated `/presigned/...` path. */
export function encodePresignedIntegrationPath(value: string): string {
  const segments = value.trim().split('/').map(decodeSegment);
  return segments
    .map((segment, index) => (index === 0 ? '' : encodeURIComponent(segment)))
    .join('/');
}

/**
 * The integration flag's `s3_url` when it is a `/presigned/` edge-lambda path.
 * Any other value (including a path that only looks similar) returns null.
 */
export function presignedIntegrationPath(
  params: Record<string, string> | undefined,
): string | null {
  const value = params?.s3_url?.trim() ?? '';
  if (!value.startsWith(PRESIGNED_INTEGRATION_PREFIX)) return null;
  return isPresignedIntegrationPath(value) ? value : null;
}

/** Pull an https presigned URL out of an edge-lambda 302 or JSON body. */
export function presignedUrlFromEdgeResult(input: {
  status: number;
  location: string | null;
  json: unknown;
}): string | null {
  if (input.status >= 300 && input.status < 400) {
    const location = input.location?.trim() ?? '';
    return /^https:\/\//i.test(location) ? location : null;
  }
  if (input.status < 200 || input.status >= 300) return null;
  if (!input.json || typeof input.json !== 'object') return null;
  const record = input.json as Record<string, unknown>;
  for (const key of ['s3_url', 'signedUrl', 'presignedUrl', 'url']) {
    const candidate = record[key];
    if (typeof candidate === 'string' && /^https:\/\//i.test(candidate.trim())) {
      return candidate.trim();
    }
  }
  return null;
}
